import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent } from "components/ui/Dialog";
import { useForm } from "react-hook-form";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import * as z from "zod";
import toast from "react-hot-toast";
import type {
  IChangeCountryMutation,
  IChangeCountryMutationVariables,
} from "lib/queries/settings/__generated__/ChangeCountryMutation.generated";
import ChangeCountryMutation from "lib/queries/settings/ChangeCountryMutation";
import { Form, FormField, FormMessage } from "components/ui/Form";
import { Button } from "components/ui/Button";
import useSelfie from "lib/hooks/useSelfie";
import CountryCombobox from "components/settings/CountryCombobox";

export default function SelectCountryDialog() {
  const intl = useIntl();
  const viewer = useSelfie();

  const [changeCountry] = useMutation<IChangeCountryMutation, IChangeCountryMutationVariables>(ChangeCountryMutation);

  const form = useForm<SelectCountryDataShape>({
    mode: "onSubmit",
    resolver: zodResolver(selectCountryDataShape(intl)),
    defaultValues: {
      country: "",
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const onSubmit = async (values: SelectCountryDataShape) => {
    await toast.promise(
      changeCountry({
        variables: {
          country: values.country,
        },
      }),
      {
        loading: intl.formatMessage({ defaultMessage: "Aguarde...", id: "VZazm7" }),
        success: intl.formatMessage({
          defaultMessage: "País alterado com sucesso!",
          id: "0e0Sr8",
        }),
        error: intl.formatMessage({
          defaultMessage: "Não foi possível alterar seu País agora. Tente novamente mais tarde.",
          id: "nWrDev",
        }),
      },
    );
  };

  if (!viewer) {
    return null;
  }

  if (viewer.country?.trim()) {
    return null;
  }

  return (
    <Dialog open={true}>
      <DialogContent className="sm:rounded-3xl" hideCloseButton={true}>
        <div className="space-y-1.5 text-center">
          <h1 className="m-0 text-2xl font-bold">
            <FormattedMessage defaultMessage="Em qual país você mora?" id="Q7MSap" />
          </h1>

          <p className="text-sm font-normal tracking-wide text-slate-500">
            <FormattedMessage
              defaultMessage="Isso nos ajuda a configurar sua experiência no site de forma personalizada."
              id="uJRf6M"
            />
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-y-5">
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => {
                return (
                  <div className="space-y-2">
                    <CountryCombobox country={field.value} withoutLabel={true} onCountryChange={field.onChange} />

                    <FormMessage />
                  </div>
                );
              }}
            />

            <Button type="submit" variant="default" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
              ) : (
                <FormattedMessage defaultMessage="Atualizar cadastro" id="LrnqkZ" />
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function selectCountryDataShape(intl: IntlShape) {
  return z.object({
    country: z.string({
      required_error: intl.formatMessage({
        defaultMessage: "Selecione o país",
        id: "hpY0Ff",
      }),
    }),
  });
}
type SelectCountryDataShape = z.infer<ReturnType<typeof selectCountryDataShape>>;
